<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useAlert } from 'dashboard/composables';
import HotelBookingsAPI from 'dashboard/api/hotelBookings';
import MessageApi from 'dashboard/api/inbox/message';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
  contact: { type: Object, required: true },
  quoteData: { type: Object, required: true },
  booking: { type: Object, default: null },
});

const emit = defineEmits(['saved', 'cancel']);

const isSubmitting = ref(false);
const isSending = ref(false);

const checkIn = ref(props.booking?.check_in || '');
const checkOut = ref(props.booking?.check_out || '');
const roomId = ref(props.booking?.motor_reserva_room_id || '');
const rateId = ref(props.booking?.motor_reserva_rate_id || '');
const adults = ref(props.booking?.adults || 1);
const children = ref(props.booking?.children || 0);
const children2 = ref(props.booking?.children2 || 0);
const isHoliday = ref(props.booking?.is_holiday || false);
const notes = ref(props.booking?.notes || '');
const totalPrice = ref(props.booking?.total_price || 0);
const selectedExtras = ref({});

const availableRooms = ref([]);
const calendarPrices = ref({});
const isLoadingRooms = ref(false);
const datesSelected = computed(() => checkIn.value && checkOut.value);

const rooms = computed(() =>
  availableRooms.value.length > 0
    ? availableRooms.value
    : props.quoteData?.rooms || []
);
const rates = computed(() => props.quoteData?.rates || []);
const roomRates = computed(() => props.quoteData?.room_rates || []);
const extras = computed(() => props.quoteData?.extras || []);

const selectedRoom = computed(() =>
  rooms.value.find(r => r.id === Number(roomId.value))
);

const availableRates = computed(() => {
  if (!roomId.value) return [];
  const rateIds = roomRates.value
    .filter(rr => rr.motor_reserva_room_id === Number(roomId.value))
    .map(rr => rr.motor_reserva_rate_id);
  return rates.value.filter(r => rateIds.includes(r.id));
});

const selectedRate = computed(() =>
  rates.value.find(r => r.id === Number(rateId.value))
);

const selectedRoomRate = computed(() => {
  if (!roomId.value || !rateId.value) return null;
  return roomRates.value.find(
    rr =>
      rr.motor_reserva_room_id === Number(roomId.value) &&
      rr.motor_reserva_rate_id === Number(rateId.value)
  );
});

const pensionLabels = {
  room_only: 'Sem pensao',
  breakfast: 'Cafe da manha',
  half_board: 'Meia pensao',
  full_board: 'Pensao completa',
  all_inclusive: 'All inclusive',
};

const pensionLabel = computed(
  () => pensionLabels[selectedRate.value?.pension_type] || ''
);

watch(roomId, () => {
  rateId.value = '';
  if (selectedRoom.value) {
    adults.value = selectedRoom.value.max_adults || 1;
  }
});

const fetchAvailableRooms = async () => {
  if (!checkIn.value || !checkOut.value) return;
  isLoadingRooms.value = true;
  try {
    const { data } = await HotelBookingsAPI.getAvailableRooms(
      checkIn.value,
      checkOut.value
    );
    availableRooms.value = data.rooms || data;
    calendarPrices.value = data.calendar_prices || {};
    if (!(data.rooms || data).find(r => r.id === Number(roomId.value))) {
      roomId.value = '';
      rateId.value = '';
    }
  } catch {
    availableRooms.value = [];
  } finally {
    isLoadingRooms.value = false;
  }
};

watch(
  () => checkIn.value,
  newVal => {
    if (newVal) {
      const d = new Date(newVal + 'T00:00:00');
      d.setDate(d.getDate() + 1);
      checkOut.value = d.toISOString().split('T')[0];
    }
  }
);

watch(
  () => checkOut.value,
  () => {
    if (checkIn.value && checkOut.value) fetchAvailableRooms();
  }
);

const nights = computed(() => {
  if (!checkIn.value || !checkOut.value) return 0;
  const diff = Math.round(
    (new Date(checkOut.value) - new Date(checkIn.value)) / 86400000
  );
  return diff > 0 ? diff : 0;
});

const getPrice = (rr, n, prefix) => {
  switch (n) {
    case 1:
      return Number(rr[`${prefix}1_night`]) || 0;
    case 2:
      return Number(rr[`${prefix}2_nights`]) || 0;
    case 3:
      return Number(rr[`${prefix}3_nights`]) || 0;
    default: {
      const base = Number(rr[`${prefix}3_nights`]) || 0;
      return base > 0 ? Math.round((base / 3) * n * 100) / 100 : 0;
    }
  }
};

const accommodationPrice = computed(() => {
  if (!selectedRoomRate.value || nights.value <= 0) return 0;

  // Try calendar prices first (sum of daily prices for the period)
  const calEntry = calendarPrices.value[selectedRoomRate.value.id];
  if (calEntry && calEntry.nights > 0) {
    const total =
      adults.value >= 2 ? calEntry.price_2_total : calEntry.price_1_total;
    if (total > 0) return total;
  }

  // Fallback to base room_rate prices
  const prefix = isHoliday.value ? 'holiday_price_' : 'price_';
  return getPrice(selectedRoomRate.value, nights.value, prefix);
});

const extrasTotal = computed(() => {
  let total = 0;
  extras.value.forEach(extra => {
    const qty = selectedExtras.value[extra.id] || 0;
    if (qty <= 0) return;
    let extPrice = Number(extra.price) * qty;
    if (extra.price_type === 'per_night') extPrice *= nights.value;
    else if (extra.price_type === 'per_person')
      extPrice *= Number(adults.value) + Number(children.value);
    total += extPrice;
  });
  return total;
});

const calculatedPrice = computed(
  () => accommodationPrice.value + extrasTotal.value
);

watch(calculatedPrice, newVal => {
  totalPrice.value = newVal;
});

const formatCurrency = value =>
  Number(value || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });

const formatDate = date => {
  if (!date) return '-';
  return new Date(date + 'T00:00:00').toLocaleDateString('pt-BR');
};

const isFormValid = computed(
  () =>
    roomId.value &&
    rateId.value &&
    checkIn.value &&
    checkOut.value &&
    nights.value > 0 &&
    adults.value > 0
);

const buildPayload = () => ({
  hotel_booking: {
    motor_reserva_room_id: Number(roomId.value),
    motor_reserva_rate_id: Number(rateId.value),
    contact_id: props.contact?.id,
    conversation_id: props.conversationId,
    check_in: checkIn.value,
    check_out: checkOut.value,
    guests:
      Number(adults.value) + Number(children.value) + Number(children2.value),
    adults: Number(adults.value),
    children: Number(children.value),
    children2: Number(children2.value),
    is_holiday: isHoliday.value,
    total_price: Number(totalPrice.value),
    notes: notes.value,
    status: 'budget',
    booking_type: 'hospedagem',
    extras_data: selectedExtras.value,
  },
});

const handleSubmit = async () => {
  if (!isFormValid.value) return;
  isSubmitting.value = true;
  try {
    if (props.booking?.id) {
      await HotelBookingsAPI.update(props.booking.id, buildPayload());
      useAlert('Orcamento atualizado!');
    } else {
      await HotelBookingsAPI.create(buildPayload());
      useAlert('Orcamento criado!');
    }
    emit('saved');
  } catch (error) {
    const msg = error?.response?.data?.message || error?.response?.data?.error;
    useAlert(msg || 'Erro ao salvar orcamento.');
  } finally {
    isSubmitting.value = false;
  }
};

const buildExtrasText = () => {
  const lines = [];
  extras.value.forEach(extra => {
    const qty = selectedExtras.value[extra.id] || 0;
    if (qty <= 0) return;
    let extPrice = Number(extra.price) * qty;
    if (extra.price_type === 'per_night') extPrice *= nights.value;
    else if (extra.price_type === 'per_person')
      extPrice *= Number(adults.value) + Number(children.value);
    lines.push(`  + ${extra.name} (${qty}x): ${formatCurrency(extPrice)}`);
  });
  return lines.length > 0
    ? `\n🎁 *Extras:*\n${lines.join('\n')}\n💎 Subtotal extras: ${formatCurrency(extrasTotal.value)}`
    : '';
};

const buildDefaultMessage = () => {
  const room = selectedRoom.value;
  const rate = selectedRate.value;
  const extrasText = buildExtrasText();
  const obsText = notes.value ? `\n📝 *Obs:* ${notes.value}` : '';

  return [
    `🏨 *Orcamento de Hospedagem*${isHoliday.value ? ' (Feriado)' : ''}`,
    '',
    `📋 *Quarto:* ${room?.name || '-'}`,
    `📊 *Tarifa:* ${rate?.name || '-'}`,
    pensionLabel.value ? `🍽️ *Pensao:* ${pensionLabel.value}` : null,
    '',
    `👤 *Adultos:* ${adults.value}`,
    children.value > 0 ? `👶 *Criancas:* ${children.value}` : null,
    children2.value > 0 ? `👦 *Criancas F2:* ${children2.value}` : null,
    '',
    `📅 *Check-in:* ${formatDate(checkIn.value)}${props.quoteData.check_in_time ? ` as ${props.quoteData.check_in_time}` : ''}`,
    `📅 *Check-out:* ${formatDate(checkOut.value)}${props.quoteData.check_out_time ? ` as ${props.quoteData.check_out_time}` : ''}`,
    `🌙 *Diarias:* ${nights.value}`,
    '',
    `💰 *Hospedagem:* ${formatCurrency(accommodationPrice.value)}`,
    extrasText || null,
    '',
    `✅ *TOTAL: ${formatCurrency(totalPrice.value)}*`,
    props.quoteData.property_name
      ? `\n🏨 ${props.quoteData.property_name}`
      : null,
    obsText || null,
  ]
    .filter(Boolean)
    .join('\n');
};

const buildMessageFromTemplate = template => {
  const room = selectedRoom.value;
  const rate = selectedRate.value;
  const extrasText = buildExtrasText();
  const obsText = notes.value ? `📝 *Obs:* ${notes.value}` : '';

  return template
    .replace(/\{id\}/g, props.booking?.id || 'novo')
    .replace(/\{quarto\}/g, room?.name || '-')
    .replace(/\{tarifa\}/g, rate?.name || '-')
    .replace(/\{pensao\}/g, pensionLabel.value || '-')
    .replace(/\{adultos\}/g, adults.value)
    .replace(/\{criancas\}/g, Number(children.value) + Number(children2.value))
    .replace(/\{checkin\}/g, formatDate(checkIn.value))
    .replace(/\{checkout\}/g, formatDate(checkOut.value))
    .replace(/\{checkin_hora\}/g, props.quoteData.check_in_time || '')
    .replace(/\{checkout_hora\}/g, props.quoteData.check_out_time || '')
    .replace(/\{diarias\}/g, nights.value)
    .replace(/\{valor_diaria\}/g, formatCurrency(accommodationPrice.value))
    .replace(/\{valor_extras\}/g, formatCurrency(extrasTotal.value))
    .replace(/\{valor_total\}/g, formatCurrency(totalPrice.value))
    .replace(/\{extras\}/g, extrasText)
    .replace(/\{observacoes\}/g, obsText)
    .replace(/\{hotel\}/g, props.quoteData.property_name || '');
};

const saveAndSend = async () => {
  if (!isFormValid.value) return;
  isSending.value = true;
  try {
    let bookingId = props.booking?.id;
    if (!bookingId) {
      const { data } = await HotelBookingsAPI.create(buildPayload());
      bookingId = data.id;
    } else {
      await HotelBookingsAPI.update(bookingId, buildPayload());
    }

    const template = props.quoteData.orcamento_message_template;
    const message = template
      ? buildMessageFromTemplate(template)
      : buildDefaultMessage();

    await MessageApi.create({
      conversationId: props.conversationId,
      message,
      private: false,
    });

    useAlert('Orcamento salvo e enviado na conversa!');
    emit('saved');
  } catch (error) {
    useAlert('Erro ao enviar orcamento.');
  } finally {
    isSending.value = false;
  }
};

const priceTypeLabels = {
  per_night: '/noite',
  per_stay: '/estadia',
  per_person: '/pessoa',
};

onMounted(() => {
  if (props.booking?.extras_data) {
    selectedExtras.value = { ...props.booking.extras_data };
  }
  if (props.booking?.check_in && props.booking?.check_out) {
    fetchAvailableRooms();
  }
});

const ic =
  'w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 text-sm text-n-slate-12 focus:border-n-brand-9 focus:outline-none';
</script>

<template>
  <form class="flex flex-col gap-4 p-4" @submit.prevent="handleSubmit">
    <!-- Header -->
    <div
      class="flex items-center gap-2 p-3 bg-n-brand-2 rounded-lg border border-n-brand-7"
    >
      <span class="text-xl">🏨</span>
      <div>
        <div class="text-sm font-semibold text-n-brand-11">
          Orcamento Motor de Reserva
        </div>
        <div class="text-xs text-n-brand-10">
          Selecione quarto, tarifa e extras para calcular o orcamento
        </div>
      </div>
    </div>

    <!-- Dates -->
    <div class="grid grid-cols-2 gap-4">
      <div>
        <label class="block text-xs font-medium text-n-slate-11 mb-1"
          >Check-in</label
        >
        <input v-model="checkIn" type="date" :class="ic" />
      </div>
      <div>
        <label class="block text-xs font-medium text-n-slate-11 mb-1"
          >Check-out</label
        >
        <input v-model="checkOut" type="date" :class="ic" />
      </div>
    </div>
    <div v-if="nights > 0" class="text-xs text-n-brand-11 font-medium">
      {{ nights }} {{ nights === 1 ? 'diaria' : 'diarias' }}
    </div>

    <!-- Loading rooms -->
    <div
      v-if="isLoadingRooms"
      class="flex items-center gap-2 text-xs text-n-slate-10 py-2"
    >
      <span
        class="animate-spin w-4 h-4 border-2 border-n-brand-9 border-t-transparent rounded-full"
      />
      Verificando disponibilidade...
    </div>

    <!-- Room -->
    <div v-if="datesSelected && !isLoadingRooms">
      <label class="block text-xs font-medium text-n-slate-11 mb-1"
        >Quarto</label
      >
      <select v-model="roomId" :class="ic">
        <option value="" disabled>Selecione o quarto...</option>
        <option v-for="room in rooms" :key="room.id" :value="room.id">
          {{ room.name }}
          {{ room.room_type ? `(${room.room_type})` : '' }}
          - Max {{ room.max_occupancy }} hosp.
          {{
            room.available_units != null
              ? `(${room.available_units} disp.)`
              : ''
          }}
        </option>
      </select>
      <p
        v-if="availableRooms.length === 0 && !isLoadingRooms && datesSelected"
        class="text-xs text-red-500 mt-1"
      >
        Nenhum quarto disponivel para estas datas.
      </p>
    </div>

    <!-- Room info -->
    <div
      v-if="selectedRoom"
      class="bg-n-slate-2 rounded-lg p-3 text-xs text-n-slate-11"
    >
      <div v-if="selectedRoom.description" class="mb-1 text-n-slate-10">
        {{ selectedRoom.description }}
      </div>
      <div class="flex gap-4 flex-wrap">
        <span
          ><span class="font-medium">Max adultos:</span>
          {{ selectedRoom.max_adults }}</span
        >
        <span
          ><span class="font-medium">Max criancas:</span>
          {{ selectedRoom.max_children }}</span
        >
        <span v-if="selectedRoom.available_units != null"
          ><span class="font-medium">Disponiveis:</span>
          {{ selectedRoom.available_units }}/{{
            selectedRoom.total_units
          }}</span
        >
        <span v-else
          ><span class="font-medium">Unidades:</span>
          {{ selectedRoom.total_units }}</span
        >
      </div>
    </div>

    <!-- Rate -->
    <div v-if="roomId">
      <label class="block text-xs font-medium text-n-slate-11 mb-1"
        >Tarifa</label
      >
      <select v-model="rateId" :class="ic">
        <option value="" disabled>Selecione a tarifa...</option>
        <option v-for="rate in availableRates" :key="rate.id" :value="rate.id">
          {{ rate.name }}
          {{
            rate.pension_type
              ? `(${pensionLabels[rate.pension_type] || rate.pension_type})`
              : ''
          }}
        </option>
      </select>
    </div>

    <!-- Rate info -->
    <div
      v-if="selectedRate"
      class="bg-n-slate-2 rounded-lg p-3 text-xs text-n-slate-11"
    >
      <div v-if="selectedRate.description" class="mb-1">
        {{ selectedRate.description }}
      </div>
      <div class="flex gap-4 flex-wrap">
        <span v-if="selectedRate.min_nights"
          ><span class="font-medium">Min noites:</span>
          {{ selectedRate.min_nights }}</span
        >
        <span v-if="selectedRate.max_nights"
          ><span class="font-medium">Max noites:</span>
          {{ selectedRate.max_nights }}</span
        >
      </div>
    </div>

    <!-- Holiday toggle -->
    <div
      v-if="rateId"
      class="flex items-center gap-3 p-3 rounded-lg"
      :class="
        isHoliday
          ? 'bg-n-amber-2 border border-n-amber-7'
          : 'bg-n-slate-2 border border-n-weak'
      "
    >
      <input
        id="mr-holiday"
        v-model="isHoliday"
        type="checkbox"
        class="rounded border-n-weak text-n-amber-9 focus:ring-n-amber-9"
      />
      <label
        for="mr-holiday"
        class="text-sm font-medium"
        :class="isHoliday ? 'text-n-amber-11' : 'text-n-slate-11'"
        >Tarifa de Feriado</label
      >
    </div>

    <!-- Dates (already shown above) -->

    <!-- Guests -->
    <div v-if="rateId" class="grid grid-cols-3 gap-3">
      <div>
        <label class="block text-xs font-medium text-n-slate-11 mb-1"
          >Adultos</label
        >
        <input v-model.number="adults" type="number" min="1" :class="ic" />
      </div>
      <div>
        <label class="block text-xs font-medium text-n-slate-11 mb-1"
          >Criancas</label
        >
        <input v-model.number="children" type="number" min="0" :class="ic" />
      </div>
      <div>
        <label class="block text-xs font-medium text-n-slate-11 mb-1"
          >Criancas F2</label
        >
        <input v-model.number="children2" type="number" min="0" :class="ic" />
      </div>
    </div>

    <!-- Extras -->
    <div v-if="rateId && extras.length > 0">
      <label class="block text-xs font-medium text-n-slate-11 mb-2"
        >Extras</label
      >
      <div class="flex flex-col gap-2">
        <div
          v-for="extra in extras"
          :key="extra.id"
          class="flex items-center justify-between p-2 rounded-lg border border-n-weak bg-n-surface-1"
        >
          <div class="flex-1">
            <span class="text-sm text-n-slate-12">{{ extra.name }}</span>
            <span class="text-xs text-n-slate-10 ml-1">
              {{ formatCurrency(extra.price)
              }}{{ priceTypeLabels[extra.price_type] || '' }}
            </span>
          </div>
          <input
            v-model.number="selectedExtras[extra.id]"
            type="number"
            min="0"
            class="w-16 rounded-lg border border-n-weak bg-n-surface-1 px-2 py-1 text-sm text-n-slate-12 text-center focus:border-n-brand-9 focus:outline-none"
            placeholder="0"
          />
        </div>
      </div>
    </div>

    <!-- Price breakdown -->
    <div
      v-if="selectedRoomRate && nights > 0"
      class="bg-n-slate-2 rounded-lg p-3 text-xs"
    >
      <div v-if="isHoliday" class="text-n-amber-11 font-medium mb-2">
        Tarifa de Feriado
      </div>
      <div class="flex justify-between text-n-slate-11">
        <span>Hospedagem ({{ nights }} diaria(s))</span>
        <span>{{ formatCurrency(accommodationPrice) }}</span>
      </div>
      <template v-for="extra in extras" :key="'sum-' + extra.id">
        <div
          v-if="selectedExtras[extra.id] > 0"
          class="flex justify-between text-n-slate-11 mt-1"
        >
          <span>{{ extra.name }} ({{ selectedExtras[extra.id] }}x)</span>
          <span>{{
            formatCurrency(
              (() => {
                let p = Number(extra.price) * selectedExtras[extra.id];
                if (extra.price_type === 'per_night') p *= nights;
                else if (extra.price_type === 'per_person')
                  p *= Number(adults) + Number(children);
                return p;
              })()
            )
          }}</span>
        </div>
      </template>
      <div
        class="flex justify-between text-n-slate-12 font-semibold mt-2 pt-2 border-t border-n-weak"
      >
        <span>Total</span>
        <span class="text-base">{{ formatCurrency(calculatedPrice) }}</span>
      </div>
    </div>

    <!-- Total (editable) -->
    <div v-if="rateId">
      <label class="block text-xs font-medium text-n-slate-11 mb-1"
        >Valor Total (ajustavel)</label
      >
      <input
        v-model.number="totalPrice"
        type="number"
        step="0.01"
        min="0"
        :class="ic"
      />
    </div>

    <!-- Notes -->
    <div v-if="rateId">
      <label class="block text-xs font-medium text-n-slate-11 mb-1"
        >Observacoes</label
      >
      <textarea
        v-model="notes"
        rows="2"
        :class="ic"
        placeholder="Observacoes adicionais..."
      />
    </div>

    <!-- Actions -->
    <div v-if="rateId" class="flex flex-col gap-2 pt-2">
      <div class="flex items-center gap-2">
        <Button label="Cancelar" slate sm @click="emit('cancel')" />
        <div class="flex-1" />
        <Button
          label="Salvar"
          sm
          slate
          :disabled="!isFormValid || isSubmitting"
          :is-loading="isSubmitting"
          @click="handleSubmit"
        />
        <Button
          label="Salvar e Enviar"
          sm
          :disabled="!isFormValid || isSending"
          :is-loading="isSending"
          @click="saveAndSend"
        />
      </div>
    </div>
  </form>
</template>
